import React, { useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import CustomModal from "../../components/CustomModal";
import WarningModal from "../../components/WarningModal";

interface CanvasModalsProps {
  customModal: {
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "loading" | "confirmation" | "warning";
    onConfirm?: () => void;
  };
  setCustomModal: React.Dispatch<React.SetStateAction<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "loading" | "confirmation" | "warning";
    onConfirm?: () => void;
  }>>;
  modalRefreshNModal: boolean;
  setModalRefreshNModal: React.Dispatch<React.SetStateAction<boolean>>;
  showHowImage: boolean;
  setShowHowImage: React.Dispatch<React.SetStateAction<boolean>>;
  modalSize: string;
  imageSrc: string;
}

const CanvasModals: React.FC<CanvasModalsProps> = ({
  customModal,
  setCustomModal,
  modalRefreshNModal,
  setModalRefreshNModal,
  showHowImage,
  setShowHowImage,
  modalSize,
  imageSrc
}) => {
  const imageModalRef = useRef<HTMLDivElement>(null);

  // Close image modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (imageModalRef.current && !imageModalRef.current.contains(event.target as Node)) {
        setShowHowImage(false);
      }
    };

    if (showHowImage) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showHowImage, setShowHowImage]);

  return (
    <>
      {/* Custom Modal */}
      <CustomModal
        isOpen={customModal.isOpen}
        onClose={() => setCustomModal({ ...customModal, isOpen: false })}
        onConfirm={customModal.onConfirm}
        title={customModal.title}
        message={customModal.message}
        type={customModal.type}
      />

      {/* Warning Modal */}
      <WarningModal
        isOpen={modalRefreshNModal}
        onClose={() => setModalRefreshNModal(false)}
        message="Data will be refreshed nightly only if the assistant is published."
      />

      {/* Image Modal */}
      {showHowImage && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div
            ref={imageModalRef}
            className={`relative bg-white px-6 pt-3 pb-1 rounded-lg shadow-lg w-full ${modalSize}`}
          >
            {/* Close Button Row */}
            <div className="flex justify-end">
              <button
                onClick={() => setShowHowImage(false)}
                className="text-gray-600 hover:text-black font-bold focus:outline-none"
                aria-label="Close"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Image */}
            <div className="mt-2">
              <img
                src={imageSrc}
                alt="Example"
                className="max-w-full h-auto rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CanvasModals;